import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/base/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";
import { categories } from "@/mocks/categories";
import { videos } from "@/mocks/videos";

const scopeItems = [
  {
    icon: "ri-film-line",
    title: "电影内容",
    description:
      "收录剧情长片、类型电影与作者电影，整理类别、时长、发布年份、地区与制作信息，便于按条件筛选。",
  },
  {
    icon: "ri-movie-2-line",
    title: "短片内容",
    description:
      "收录实验、动画、纪录与剧情短片，时长集中在十五分钟以内，适合在短时间内集中浏览与比较。",
  },
  {
    icon: "ri-fire-line",
    title: "热门内容",
    description:
      "依据站内浏览量整理热门视频内容，涵盖人文纪录、美食纪实与旅行观察等题材，反映近期浏览趋势。",
  },
  {
    icon: "ri-file-list-3-line",
    title: "信息索引",
    description:
      "为每一条内容建立独立详情页，展示封面大图、简介、标签与相关信息，保证列表与详情数据一致。",
  },
];

const usageSteps = [
  { step: "01", title: "浏览分类", description: "从电影、短片、热门三个分类进入对应列表，或在首页查看焦点推荐与最新收录。" },
  { step: "02", title: "多维筛选", description: "在分类页按年份、地区与排序方式组合筛选，快速缩小浏览范围，移动端可使用侧滑筛选面板。" },
  { step: "03", title: "关键词检索", description: "在顶部搜索框输入标题、简介、导演或标签关键词，搜索结果页支持分类过滤与重新排序。" },
  { step: "04", title: "查看详情", description: "点击视频卡片进入独立详情页，查看简介、基本信息与基于分类标签的相关推荐。" },
];

export default function AboutPage() {
  usePageMeta(
    "关于我们 - 色色视频",
    "了解色色视频的站点定位、内容范围、使用方式与版权声明。色色视频是多元高清视频内容聚合展示平台，不提供播放与下载服务。",
    "关于色色视频,色色视频介绍,视频聚合平台,版权声明",
  );

  const movieCount = videos.filter((item) => item.category === "movie").length;
  const shortCount = videos.filter((item) => item.category === "short").length;
  const hotCount = videos.filter((item) => item.category === "hot").length;

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <div className="container-page pt-4 md:pt-5">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
        </div>

        <section className="container-page pt-5">
          <div className="overflow-hidden rounded-lg bg-gradient-to-br from-primary-900 via-primary-800 to-primary-600 px-6 py-10 md:px-10 md:py-14">
            <p className="font-label text-[10px] font-semibold uppercase tracking-[0.24em] text-accent-300">
              About Sese Video
            </p>
            <h1 className="mt-3 text-2xl font-semibold text-background-50 md:text-[36px]">
              关于色色视频
            </h1>
            <p className="mt-3 max-w-2xl text-xs leading-6 text-background-200 md:text-sm md:leading-7">
              色色视频是一个面向广泛视频内容爱好者的多元高清视频内容聚合平台。我们围绕电影、短片与热门内容建立清晰的分类体系，用整齐的信息结构与便捷的筛选方式，帮助用户在大量内容中快速找到想看的那一部。
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <a
                href="mailto:hello@seseshipin.example.com"
                className="focus-ring inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-xs font-semibold text-primary-950 transition-colors hover:bg-accent-400"
              >
                <i className="ri-mail-send-line text-sm" aria-hidden="true" />
                反馈与联系
              </a>
              <Link
                to="/category/all"
                className="focus-ring inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-50/30 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-background-50/10"
              >
                浏览全部内容
                <i className="ri-arrow-right-line text-sm" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>

        <section className="container-page pt-8 md:pt-10" aria-labelledby="positioning-heading">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-7">
              <h2
                id="positioning-heading"
                className="text-xl font-semibold text-foreground-950 md:text-2xl"
              >
                站点定位与内容范围
              </h2>
              <p className="mt-3 text-xs leading-6 text-foreground-600 md:text-sm md:leading-7">
                色色视频不生产视频内容，也不提供在线播放、下载或会员服务。我们的工作是把分散在不同地区的视频信息整理成可浏览、可筛选的索引：为每条内容建立独立详情页，统一封面、标题、时长、年份、地区与简介等字段，让浏览体验保持一致。
              </p>
              <p className="mt-3 text-xs leading-6 text-foreground-600 md:text-sm md:leading-7">
                内容覆盖华语、欧美、日韩等多个地区，题材横跨剧情、悬疑、科幻、动画、美食与人文纪实。所有列表信息与详情页信息使用同一份数据源，避免出现展示不一致的情况。
              </p>

              <div className="mt-5 grid grid-cols-3 gap-3">
                {[
                  { label: "电影", value: movieCount },
                  { label: "短片", value: shortCount },
                  { label: "热门", value: hotCount },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="rounded-md border border-background-200/90 bg-white px-3 py-3 text-center"
                  >
                    <p className="font-heading text-xl font-semibold text-primary-600">
                      {item.value}
                    </p>
                    <p className="mt-1 text-[11px] text-foreground-500">{item.label}内容</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="h-[240px] w-full overflow-hidden rounded-lg border border-background-200/90 bg-background-200 md:h-[300px]">
                <img
                  src="https://readdy.ai/api/search-image?query=Minimal%20editorial%20still%20life%20of%20a%20dark%20navy%20desk%20with%20stacked%20film%20reels%2C%20a%20paper%20catalogue%20and%20a%20small%20warm%20amber%20desk%20lamp%20casting%20soft%20light%2C%20clean%20organized%20composition%2C%20film%20grain%20texture%2C%20no%20text%2C%20muted%20cool%20tones%20with%20warm%20accents%2C%20high%20detail&width=800&height=600&seq=about-editorial-01&orientation=squarish"
                  alt="工作台上整理视频资料与片盘，象征视频内容聚合与索引"
                  title="色色视频 - 内容聚合与信息索引"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-9 md:py-12" aria-labelledby="scope-heading">
          <div className="container-page">
            <div className="mb-5 md:mb-6">
              <p className="mb-1 flex items-center gap-2 font-label text-[11px] font-semibold uppercase tracking-[0.18em] text-secondary-600">
                <span className="h-[2px] w-5 rounded-full bg-accent-500" aria-hidden="true" />
                Content Scope
              </p>
              <h2 id="scope-heading" className="text-xl font-semibold text-foreground-950 md:text-2xl">
                我们收录什么
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4">
              {scopeItems.map((item) => (
                <div
                  key={item.title}
                  className="rounded-lg border border-background-200/90 bg-white p-4 md:p-5"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                      <i className={`${item.icon} text-lg`} aria-hidden="true" />
                    </span>
                    <h3 className="text-sm font-semibold text-foreground-900 md:text-base">
                      {item.title}
                    </h3>
                  </div>
                  <p className="mt-3 text-[11px] leading-relaxed text-foreground-500 md:text-xs md:leading-6">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {categories
                .filter((item) => item.key !== "all")
                .map((item) => (
                  <Link
                    key={item.key}
                    to={`/category/${item.key}`}
                    className="focus-ring inline-flex items-center gap-1.5 rounded-full border border-background-300/80 bg-white px-3 py-1.5 text-[11px] text-foreground-600 transition-colors hover:border-secondary-300 hover:text-secondary-700 md:text-xs"
                  >
                    <i className={`${item.icon} text-sm`} aria-hidden="true" />
                    前往{item.name}分类
                  </Link>
                ))}
            </div>
          </div>
        </section>

        <section className="container-page py-9 md:py-12" aria-labelledby="usage-heading">
          <div className="mb-5 md:mb-6">
            <p className="mb-1 flex items-center gap-2 font-label text-[11px] font-semibold uppercase tracking-[0.18em] text-secondary-600">
              <span className="h-[2px] w-5 rounded-full bg-accent-500" aria-hidden="true" />
              How to Browse
            </p>
            <h2 id="usage-heading" className="text-xl font-semibold text-foreground-950 md:text-2xl">
              如何使用本站
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
            {usageSteps.map((item) => (
              <div
                key={item.step}
                className="flex items-start gap-3 rounded-lg border border-background-200/90 bg-white p-4 md:p-5"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-50 font-label text-[11px] font-semibold text-accent-800">
                  {item.step}
                </span>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900">{item.title}</h3>
                  <p className="mt-1 text-[11px] leading-relaxed text-foreground-500 md:text-xs">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="container-page pb-9 md:pb-12" aria-labelledby="copyright-heading">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <div className="surface-card h-full p-5 md:p-6">
                <h2
                  id="copyright-heading"
                  className="flex items-center gap-2 text-base font-semibold text-foreground-950"
                >
                  <i className="ri-copyright-line text-lg text-primary-600" aria-hidden="true" />
                  版权声明
                </h2>
                <div className="mt-3 space-y-3 text-[11px] leading-6 text-foreground-600 md:text-xs">
                  <p>
                    本站为视频内容聚合展示平台，仅对公开视频信息进行整理、分类与索引，不存储、不转码、不托管任何视频文件，亦不提供在线播放与下载入口。
                  </p>
                  <p>
                    站内展示的封面图与文字简介仅用于内容识别与信息说明。相关视频、图片及文字内容的著作权归原权利人所有。如权利人认为本站展示的信息侵犯其合法权益，可通过下方邮箱提交权属证明与具体页面链接，我们将在核实后及时处理。
                  </p>
                  <p>
                    本站的分类结构、页面排版与信息整理方式由本站创作，未经许可请勿以镜像站、采集站等形式整体复制使用。
                  </p>
                  <p className="text-foreground-400">
                    演示说明：本页内容为站点演示数据，部分视频与制作信息为示例内容，相关数据接口需后续接入。
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="surface-card h-full p-5 md:p-6">
                <h2 className="text-base font-semibold text-foreground-950">联系方式</h2>
                <p className="mt-2 text-[11px] leading-relaxed text-foreground-500 md:text-xs">
                  内容纠错、版权说明、合作咨询均可通过邮件联系我们。为便于处理，请在邮件中附上具体页面链接与问题说明。
                </p>

                <a
                  href="mailto:hello@seseshipin.example.com"
                  className="focus-ring mt-4 flex items-center gap-3 rounded-md border border-background-200/90 bg-background-50 px-3 py-3 transition-colors hover:border-secondary-300"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
                    <i className="ri-mail-line text-base" aria-hidden="true" />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-label text-[10px] uppercase tracking-[0.16em] text-foreground-400">
                      Feedback Email
                    </span>
                    <span className="mt-0.5 block truncate text-xs font-medium text-foreground-900">
                      hello@seseshipin.example.com
                    </span>
                  </span>
                </a>

                <div className="mt-4 flex items-center gap-2 rounded-md bg-secondary-50 px-3 py-2.5">
                  <i className="ri-time-line text-sm text-secondary-700" aria-hidden="true" />
                  <p className="text-[11px] text-foreground-600">
                    通常会在 2 个工作日内回复处理结果
                  </p>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <Link
                    to="/"
                    className="focus-ring inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-2 text-[11px] font-semibold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-home-5-line text-sm" aria-hidden="true" />
                    返回首页
                  </Link>
                  <Link
                    to="/search"
                    className="focus-ring inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300/80 bg-white px-3 py-2 text-[11px] font-medium text-foreground-700 transition-colors hover:border-secondary-300"
                  >
                    <i className="ri-search-line text-sm" aria-hidden="true" />
                    站内搜索
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}